import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  MotifTable,
  MotifFormField,
  MotifSearch,
  MotifTableHeaderRenderer,
  MotifToast,
} from "@ey-xd/motif-react";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchEngagementDelegationList,
  resetEngagementDelegationState,
} from "../../../store/slices/EngagementDelegationSlice";
import type { EngagementDelegation as EngagementDelegationRow } from "../../../store/slices/EngagementDelegationSlice";
import {
  PAGE_NO_ACCESS,
  customPaginationPageSize,
  customPaginationPageSizeSelector,
} from "../../../Constant";
import NoAccess from "../../NoAccess";
import { getEngDelegationColumns } from "./engagementTableColumn.tsx";
import EditEngagementDelegationModal from "../../EditEngagementDelegationModal";
import ErrorOrderPage from "../../../components/ErrorOrderPage/ErrorOrderPage";
import errorImage from "../../../assets/images/empty_product_list_default.svg";
import Loader from "../../Loader/Loader";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import "./EngagementDelegation.scss";

const EngagementDelegation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isGridReady, setIsGridReady] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEngagement, setSelectedEngagement] =
    useState<EngagementDelegationRow | null>(null);
  const [toastMessage, setToastMessage] = useState<{
    variant: "success";
    message: string;
  } | null>(null);
  const gridApiRef = useRef<any>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );
  const { data, isLoading, error } = useSelector(
    (state: RootState) => state.EngagementDelegationSlice,
  );
  const isInitialLoading = isLoading && data.length === 0;

  const handleEdit = useCallback((row: EngagementDelegationRow) => {
    setSelectedEngagement(row);
    setShowEditModal(true);
  }, []);

  const tableColumns = useMemo(
    () => getEngDelegationColumns(handleEdit),
    [handleEdit],
  );

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedEngagement(null);
  }, []);

  const handleEngagementSaveSuccess = () => {
    handleCloseEditModal();
    setToastMessage({
      variant: "success",
      message: "Engagement changes saved",
    });
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (searchText && searchContainerRef.current) {
      const input = searchContainerRef.current.querySelector("input");
      if (input) input.focus();
    }
  });

  useEffect(() => {
    gridApiRef.current?.refreshCells?.({
      columns: ["EngagementOwnerName", "EngagementProxyDetails"],
      force: true,
    });
  }, [data]);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const hasEngagementOwnerAccess = useMemo(() => {
    if (!userPermissionsData?.length) return false;
    const engagementPermissions = ["engagement_partner", "engagement_delegate"];
    return userPermissionsData.some((permission) =>
      engagementPermissions.includes(permission?.toLowerCase()),
    );
  }, [userPermissionsData]);

  useEffect(() => {
    if (!hasEngagementOwnerAccess) return;

    setIsGridReady(false);
    dispatch(fetchEngagementDelegationList());
    return () => {
      dispatch(resetEngagementDelegationState());
    };
  }, [dispatch, hasEngagementOwnerAccess]);

  if (!hasEngagementOwnerAccess) {
    return (
      <NoAccess
        headerText={PAGE_NO_ACCESS.HEADER}
        descriptionText={PAGE_NO_ACCESS.DESCRIPTION}
        descriptionText2={PAGE_NO_ACCESS.SUGGESTION}
        buttonLabel={PAGE_NO_ACCESS.BUTTON_LABEL}
        onClickHandler={() => navigate("/")}
      />
    );
  }

  const searchComponent = (
    <>
      <MotifFormField ref={searchContainerRef}>
        <MotifSearch
          placeholder="Search by engagement, owner and proxy"
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchText(e.target.value)
          }
          onClear={() => setSearchText("")}
        />
      </MotifFormField>
    </>
  );

  return (
    <div className="engagement-delegation">
      <div className="engagement-delegation-container">
        {isInitialLoading && (
          <Loader className="engagement-delegation-loader" />
        )}
        {!isInitialLoading && error && (
          <>
            <div className="motif-table-title-bar">
              <div className="motif-table-info">
                <span className="motif-table-title">Engagement delegation</span>
              </div>
              {searchComponent}
            </div>
            <div className="engagement-delegation-error">
              <ErrorOrderPage
                image={errorImage}
                header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
                handleRefresh={() => {
                  setIsGridReady(false);
                  dispatch(fetchEngagementDelegationList());
                }}
              />
            </div>
          </>
        )}
        {!isInitialLoading && !error && (
          <>
            {toastMessage && (
              <div className="engagement-delegation-toast">
                <MotifToast
                  variant={toastMessage.variant}
                  onClose={() => setToastMessage(null)}
                >
                  <div slot="toast-supporting-text">{toastMessage.message}</div>
                </MotifToast>
              </div>
            )}
            <MotifTable
              className={`engagement-delegation-table ${searchText && displayedRowCount === 0 ? "hide" : ""}`}
              domLayout="autoHeight"
              headerHeight={44}
              defaultColDef={{
                wrapHeaderText: true,
                autoHeaderHeight: true,
                resizable: false,
              }}
              rowHeight={100}
              columnDefs={tableColumns}
              rowData={data}
              getRowId={(params) => params.data.EngagementId}
              quickFilterText={searchText}
              pagination
              paginationPageSize={customPaginationPageSize}
              paginationPageSizeSelector={customPaginationPageSizeSelector}
              localeText={{ pageSizeSelectorLabel: "Show" }}
              onFirstDataRendered={(params: any) => {
                gridApiRef.current = params.api;
                setIsGridReady(true);
                setDisplayedRowCount(params.api.getDisplayedRowCount());
              }}
              onFilterChanged={() => {
                if (gridApiRef.current) {
                  setDisplayedRowCount(
                    gridApiRef.current.getDisplayedRowCount(),
                  );
                }
              }}
              components={{
                agColumnHeader: MotifTableHeaderRenderer,
              }}
              suppressMovableColumns
              suppressCellFocus
              suppressHorizontalScroll={!isGridReady}
              suppressRowHoverHighlight
              title="Engagement delegation"
              titleComplement={searchComponent}
            />
            {searchText && displayedRowCount === 0 && (
              <div className="engagement-delegation-nosearch-error">
                <NoSearchResult
                  searchValue={searchText}
                  header="No data found"
                  message="Your search did not match any engagement. Please try again."
                />
              </div>
            )}
          </>
        )}
      </div>
      <EditEngagementDelegationModal
        show={showEditModal}
        engagementData={selectedEngagement}
        onClose={handleCloseEditModal}
        handleEngagementSaveSuccess={handleEngagementSaveSuccess}
      />
    </div>
  );
};

export default EngagementDelegation;
